import { DestinationEvent } from '@eventbrite/event-renderer';
import { FeatureFlags } from '@eventbrite/feature-flag-utils';
import {
    CORE_SEO_API_KEY_CONFIG,
    useFeatureFlagState,
} from '@eventbrite/splitio-checker';
import { useGate } from '@eventbrite/statsig';
import React, { useState } from 'react';
import { CategoryFilterBar } from '../../components/CategoryFilterBar';
import { categoryToIconMap } from '../../components/CategoryFilterBar/categoryToIconMap';
import {
    useGetSelectedCategory,
    useReactQueryEnabler,
    useSingleToggle,
} from '../../components/CategoryFilterBar/hooks';
import { DateOption, DATE_OPTIONS } from '../../components/DateFilterDropdown';
import {
    Breadcrumb,
    ThingsToDoBreadcrumbs,
} from '../../containers/ThingsToDoBreadcrumbs';
import { useQuerySearchService } from '../../hooks/eventHooks';
import { LocationData, NearbyPOITagShape } from '../../types/AppProps';
import { userOperatingSystemIs } from '../../utils';
import { HeroCard } from './components/HeroCard';
import { AboutPOI } from './containers/AboutPOI';
import {
    BentoCarouselSection,
    ORDERED_POI_CATEGORIES,
} from './containers/BentoCarouselSection';
import { CategoryCarousel } from './containers/CategoryCarousel/CategoryCarousel';
import { EventSection } from './containers/EventSection';
import { NearbyPOI } from './containers/NearbyPOI';
import {
    POIHeroSection,
    POIHeroSectionProps,
} from './containers/POIHeroSection';
import { PointsOfInterestFAQs } from './containers/PointsOfInterestFAQs';
import styles from './PointOfInterestPage.module.scss';

export interface PointOfInterest {
    pointOfInterestName: string;
    geometry: {
        latitude: number;
        longitude: number;
    };
    types: string[];
    photos: Object[];
    googlePlaceId: string;
    description: string;
    address: {
        formattedAddress: string;
        city: string;
        state: string;
        country: string;
    };
    htmlAttribution: string;
    imageSource: string;
    hoursOfOperation: Array<string>;
    popularEvents: Array<{
        eventName: string;
        eventSlug: string;
    }>;
}

export interface PointOfInterestProps {
    pointOfInterest: PointOfInterest;
    heroSection: POIHeroSectionProps;
    breadcrumbs: Breadcrumb[];
    nearbyPointsOfInterest: Array<NearbyPOITagShape>;
    nearbyEvents: Array<DestinationEvent>;
    freeEvents: Array<DestinationEvent>;
    nearbyNightlifeEvents: Array<DestinationEvent>;
    featureFlags: FeatureFlags;
    query_location_data: LocationData;
    userId: string | undefined;
}

export const PointOfInterestPage: React.FunctionComponent<
    PointOfInterestProps
> = (props) => {
    const {
        pointOfInterest,
        nearbyPointsOfInterest,
        breadcrumbs,
        nearbyEvents,
        featureFlags,
        freeEvents,
        nearbyNightlifeEvents,
    } = props;
    const IS_IOS_USER = userOperatingSystemIs('iOS');

    const enableNearbyPOISection = useGate('enable_poi_nearby_section').value;
    const enablePOIFAQSection = useGate('enable_poi_faq_section').value;
    const enablePOIBentoCarouselExperience =
        featureFlags.isBentoCarouselEnabled;

    const enableCostReduction = useFeatureFlagState(
        'enable_cost_reduction',
        CORE_SEO_API_KEY_CONFIG,
        { userId: props.userId ?? '' },
    );

    const enablePOICategoryFilter =
        useFeatureFlagState(
            'enable_poi_category_filter',
            CORE_SEO_API_KEY_CONFIG,
            { userId: props.userId ?? '' },
        ) && !IS_IOS_USER;
    const enablePOIDateFilter =
        useFeatureFlagState('enable_poi_date_filter', CORE_SEO_API_KEY_CONFIG, {
            userId: props.userId ?? '',
        }) && !IS_IOS_USER;
    const { shouldEnableQuery, enableQuery } = useReactQueryEnabler();
    const { setCategoryTag } = useGetSelectedCategory();
    const { handleToggled, selectedCategory, resetFiltersRef } =
        useSingleToggle({
            categories: categoryToIconMap,
        });

    const [numCarousels, setNumCarousels] = useState(5);
    const [dateSelection, changeDateSelection] = useState<DateOption>(
        DATE_OPTIONS[0],
    );

    const { data, isLoading } = useQuerySearchService({
        queryOverload: {
            page_size: 100,
            include_manual_events: true,
            include_mature_events: true,
            online_events_only: false,
            dates: [dateSelection.searchValue],
            page_type: 'POI',
            tags: [selectedCategory?.tagSelector ?? ''],
        },
        location: {
            latitude: pointOfInterest.geometry.latitude,
            longitude: pointOfInterest.geometry.longitude,
            isOnline: false,
        },
        enabled: shouldEnableQuery,
        retryOnMount: false,
    });

    const RenderEvents = () => {
        if (enablePOICategoryFilter && shouldEnableQuery) {
            return (
                <CategoryCarousel
                    category_events={data?.category_events}
                    isLoading={isLoading}
                    populatedCategoriesCount={
                        data?.carouselCount || numCarousels
                    }
                    setNumCarousels={setNumCarousels}
                    locationData={{
                        latitude: pointOfInterest.geometry.latitude,
                        longitude: pointOfInterest.geometry.longitude,
                        isOnline: false,
                    }}
                    pageType="POI"
                    handleToggle={handleToggled}
                    changeDateSelection={changeDateSelection}
                />
            );
        } else if (enablePOIBentoCarouselExperience) {
            return (
                <BentoCarouselSection
                    orderedCategories={ORDERED_POI_CATEGORIES}
                    nearbyEvents={nearbyEvents}
                    nearbyFreeEvents={freeEvents}
                    nearbyNightlifeEvents={nearbyNightlifeEvents}
                />
            );
        } else {
            return (
                <EventSection
                    pointOfInterestName={pointOfInterest.pointOfInterestName}
                    nearbyEvents={nearbyEvents}
                />
            );
        }
    };

    return (
        <>
            <>
                {enableCostReduction ? (
                    <div className={styles.costReductionTopSection}>
                        <ThingsToDoBreadcrumbs
                            containerClasses={styles.breadcrumbs}
                            breadcrumbs={breadcrumbs}
                        />
                        <HeroCard title={pointOfInterest.pointOfInterestName} />
                    </div>
                ) : (
                    <div className={styles.topSection}>
                        <div className={styles.headerBackground} />
                        <ThingsToDoBreadcrumbs
                            containerClasses={styles.breadcrumbs}
                            breadcrumbs={breadcrumbs}
                        />
                        <POIHeroSection
                            pointOfInterestName={
                                pointOfInterest.pointOfInterestName
                            }
                            imageSource={pointOfInterest.imageSource}
                            htmlAttribution={pointOfInterest.htmlAttribution}
                        />
                    </div>
                )}
            </>
            {enableCostReduction ? null : (
                <>
                    <AboutPOI
                        title={pointOfInterest.pointOfInterestName}
                        content={pointOfInterest.description}
                        address={pointOfInterest.address?.formattedAddress}
                        openingHours={pointOfInterest.hoursOfOperation}
                    />
                </>
            )}
            {enablePOICategoryFilter ? (
                <CategoryFilterBar
                    categories={categoryToIconMap}
                    pageType="POI"
                    locationData={{
                        latitude: pointOfInterest.geometry.latitude,
                        longitude: pointOfInterest.geometry.longitude,
                        isOnline: false,
                    }}
                    enableQuery={enableQuery}
                    dateSelection={dateSelection}
                    changeDateSelection={changeDateSelection}
                    setCategoryTag={setCategoryTag}
                    handleToggled={handleToggled}
                    selectedCategory={selectedCategory}
                    enableDateFilter={enablePOIDateFilter}
                    ref={resetFiltersRef}
                />
            ) : null}
            <RenderEvents />
            <div className={styles.bottomSection}>
                {enableNearbyPOISection && (
                    <NearbyPOI
                        pointOfInterestName={
                            pointOfInterest.pointOfInterestName
                        }
                        nearbyPointsOfInterest={nearbyPointsOfInterest}
                    />
                )}
                {enablePOIFAQSection && (
                    <PointsOfInterestFAQs
                        pointOfInterestName={
                            pointOfInterest.pointOfInterestName
                        }
                        hoursOfOperation={pointOfInterest.hoursOfOperation}
                        nearbyPointsOfInterest={nearbyPointsOfInterest}
                        nearbyEvents={nearbyEvents}
                    />
                )}
            </div>
        </>
    );
};
